import { useEffect, useState, useCallback } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, getDay, isSameDay, isToday } from 'date-fns';
import { getMonthVerses, setDailyVerse, removeDailyVerse, getDailyVerseSettings, setDailyVerseSettings } from '@/services/adminService';
import type { DailyVerse, Ovi } from '@dnyaneshwari/shared';
import { CHAPTERS, CHAPTER_OVI_COUNTS, CONTENT_PUBLIC_URL, DEFAULT_DAILY_VERSE_TIME } from '@dnyaneshwari/shared';

/** "05:30" -> "5:30 AM" for the copy; the inputs keep 24h. */
function fmtTime(t: string): string {
  const [h, m] = t.split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return t;
  const suffix = h < 12 ? 'AM' : 'PM';
  return `${((h + 11) % 12) + 1}:${String(m).padStart(2, '0')} ${suffix}`;
}

/** The published bundle; a staging copy can be pointed at via env. */
const CONTENT_BASE = (import.meta.env.VITE_CONTENT_BASE_URL as string | undefined) ?? CONTENT_PUBLIC_URL;

interface DayModal {
  date: Date;
  existing: DailyVerse | null;
}

export default function DailyVersePage() {
  const [current, setCurrent] = useState(new Date());
  const [verses, setVerses] = useState<Map<string, DailyVerse>>(new Map());
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState<DayModal | null>(null);
  const [selectedChapter, setSelectedChapter] = useState('01');
  const [oviNum, setOviNum] = useState('');
  const [preview, setPreview] = useState<Ovi | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  // Global send time (IST) and the draft being edited in the header.
  const [defaultTime, setDefaultTime] = useState(DEFAULT_DAILY_VERSE_TIME);
  const [timeDraft, setTimeDraft] = useState(DEFAULT_DAILY_VERSE_TIME);
  const [timeSaving, setTimeSaving] = useState(false);
  const [timeSaved, setTimeSaved] = useState(false);
  // Per-day time in the modal; starts from the global value.
  const [dayTime, setDayTime] = useState(DEFAULT_DAILY_VERSE_TIME);

  const fetchMonth = useCallback(async () => {
    setLoading(true);
    try {
      const list = await getMonthVerses(current.getFullYear(), current.getMonth());
      const map = new Map<string, DailyVerse>();
      list.forEach((v) => map.set(v.id, v));
      setVerses(map);
    } finally {
      setLoading(false);
    }
  }, [current]);

  useEffect(() => { fetchMonth(); }, [fetchMonth]);
  useEffect(() => {
    getDailyVerseSettings().then((s) => { setDefaultTime(s.sendTime); setTimeDraft(s.sendTime); });
  }, []);

  useEffect(() => {
    if (!modal) return;
    const n = parseInt(String(oviNum), 10);
    if (!Number.isInteger(n) || n < 1 || n > maxOvi) return;
    const id = setTimeout(() => { void loadPreview(); }, 500);
    return () => clearTimeout(id);
    // loadPreview reads the same state this effect keys on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [oviNum, selectedChapter, modal]);

  async function saveDefaultTime() {
    setTimeSaving(true);
    try {
      await setDailyVerseSettings({ sendTime: timeDraft });
      setDefaultTime(timeDraft);
      setTimeSaved(true);
      setTimeout(() => setTimeSaved(false), 2500);
    } finally {
      setTimeSaving(false);
    }
  }

  const maxOvi = CHAPTER_OVI_COUNTS[parseInt(selectedChapter, 10) - 1] ?? 0;
  const days = eachDayOfInterval({ start: startOfMonth(current), end: endOfMonth(current) });
  const startPad = getDay(startOfMonth(current));

  function openDay(date: Date) {
    const key = format(date, 'yyyy-MM-dd');
    const existing = verses.get(key) ?? null;
    setModal({ date, existing });
    setDayTime(existing?.sendTime || defaultTime);
    setSelectedChapter('01');
    setOviNum('');
    setPreview(null);
    setError('');
  }

  // The scripture is a static bundle, not Firestore. Validate the reference
  // against the book's own per-chapter counts, then fetch the text from the
  // published bundle for the preview -- and store it with the schedule so the
  // push has a body without a lookup.
  async function loadPreview() {
    if (!oviNum || !selectedChapter) return;
    setPreviewLoading(true);
    setPreview(null);
    const chapterNumber = parseInt(selectedChapter, 10);
    const n = parseInt(String(oviNum), 10);
    const max = CHAPTER_OVI_COUNTS[chapterNumber - 1] ?? 0;
    if (!Number.isInteger(n) || n < 1 || n > max) {
      setError(`Chapter ${chapterNumber} has ovis 1–${max}.`);
      setPreviewLoading(false);
      return;
    }
    const paddedCh = selectedChapter.padStart(2, '0');
    const oviId = `${paddedCh}-${String(n).padStart(3, '0')}`;
    try {
      const res = await fetch(`${CONTENT_BASE}/chapters/${paddedCh}.json`);
      const file = res.ok ? ((await res.json()) as { ovis: Ovi[] }) : null;
      const ovi = file?.ovis.find((o) => o.id === oviId) ?? null;
      setPreview(ovi);
      setError(ovi ? '' : `Ovi ${oviId} is in range but not in the published bundle — is the mobile site deployed?`);
    } catch {
      setError('Could not reach the published content bundle to preview the text.');
    } finally {
      setPreviewLoading(false);
    }
  }

  async function handleSave() {
    if (!modal || !oviNum) return;
    const paddedCh = selectedChapter.padStart(2, '0');
    const n = parseInt(String(oviNum), 10);
    const paddedOvi = String(n).padStart(3, '0');
    const oviId = `${paddedCh}-${paddedOvi}`;
    const max = CHAPTER_OVI_COUNTS[parseInt(selectedChapter, 10) - 1] ?? 0;
    if (!Number.isInteger(n) || n < 1 || n > max) {
      setError(`Chapter ${parseInt(selectedChapter, 10)} has ovis 1–${max}.`);
      return;
    }
    setSaving(true);
    try {
      await setDailyVerse(format(modal.date, 'yyyy-MM-dd'), oviId, paddedCh, {
        oviNumber: n,
        ...(preview?.id === oviId && preview.textMarathi ? { textMarathi: preview.textMarathi } : {}),
        // Only store an override when it differs, so changing the global
        // time later moves every ordinary day with it.
        ...(dayTime && dayTime !== defaultTime ? { sendTime: dayTime } : {}),
      });
      await fetchMonth();
      setModal(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  }

  async function handleRemove() {
    if (!modal) return;
    setSaving(true);
    try {
      await removeDailyVerse(format(modal.date, 'yyyy-MM-dd'));
      await fetchMonth();
      setModal(null);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Daily Verse Scheduler</h1>
        <p className="text-sm text-gray-500 mt-1">Set which ovi appears as "आजची ओवी" each day. A day with nothing scheduled sends nothing.</p>
      </div>

      {/* Global send time. The function ticks every 15 minutes and honours
          whatever is stored here, so this takes effect without a deploy. */}
      <div className="bg-white rounded-xl border border-gray-200 px-5 py-4 mb-4 flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[220px]">
          <p className="text-sm font-semibold text-gray-900">Notification time</p>
          <p className="text-xs text-gray-500 mt-0.5">Indian Standard Time. Applies to every day unless a day sets its own time. Currently <strong>{fmtTime(defaultTime)}</strong>.</p>
        </div>
        <input
          type="time"
          value={timeDraft}
          step={900}
          onChange={(e) => setTimeDraft(e.target.value)}
          className="px-3 py-2 border border-gray-200 rounded-lg text-sm outline-none focus:border-amber-400"
        />
        <button
          onClick={saveDefaultTime}
          disabled={timeSaving || !timeDraft || timeDraft === defaultTime}
          className="px-4 py-2 bg-amber-600 text-white rounded-lg text-sm font-semibold hover:bg-amber-700 disabled:opacity-50"
        >
          {timeSaving ? 'Saving…' : timeSaved ? 'Saved' : 'Save time'}
        </button>
      </div>

      {/* Month navigation */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <button
            onClick={() => setCurrent(new Date(current.getFullYear(), current.getMonth() - 1, 1))}
            className="p-1.5 rounded-lg hover:bg-gray-100"
          >
            <ChevronLeftIcon className="w-5 h-5 text-gray-600" />
          </button>
          <h2 className="text-base font-semibold text-gray-900">
            {format(current, 'MMMM yyyy')}
          </h2>
          <button
            onClick={() => setCurrent(new Date(current.getFullYear(), current.getMonth() + 1, 1))}
            className="p-1.5 rounded-lg hover:bg-gray-100"
          >
            <ChevronRightIcon className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* Day headers */}
        <div className="grid grid-cols-7 border-b border-gray-100">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
            <div key={d} className="text-center text-xs font-semibold text-gray-400 py-2">{d}</div>
          ))}
        </div>

        {/* Calendar grid */}
        {loading ? (
          <div className="py-16 text-center text-gray-400 text-sm">Loading…</div>
        ) : (
          <div className="grid grid-cols-7">
            {Array.from({ length: startPad }).map((_, i) => (
              <div key={`pad-${i}`} className="h-20 border-r border-b border-gray-100 bg-gray-50/50" />
            ))}
            {days.map((day) => {
              const key = format(day, 'yyyy-MM-dd');
              const verse = verses.get(key);
              const today = isToday(day);
              return (
                <button
                  key={key}
                  onClick={() => openDay(day)}
                  className={`h-20 p-2 border-r border-b border-gray-100 text-left hover:bg-amber-50 transition-colors ${today ? 'bg-amber-50' : ''}`}
                >
                  <span className={`text-xs font-semibold ${today ? 'text-amber-600' : 'text-gray-600'}`}>
                    {format(day, 'd')}
                  </span>
                  {verse && (
                    <div className="mt-1 bg-amber-100 rounded px-1.5 py-0.5">
                      <p className="text-[10px] font-medium text-amber-800 truncate">
                        {verse.oviId}{verse.sendTime ? ` · ${fmtTime(verse.sendTime)}` : ''}
                      </p>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Modal */}
      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
              <h3 className="font-semibold text-gray-900">
                {format(modal.date, 'EEEE, d MMMM yyyy')}
              </h3>
              <button onClick={() => setModal(null)} className="p-1 rounded-lg hover:bg-gray-100">
                <XMarkIcon className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              {modal.existing && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm">
                  <p className="font-semibold text-amber-800 mb-0.5">Currently scheduled:</p>
                  <p className="text-amber-700 font-mono">{modal.existing.oviId} <span className="font-sans">at {fmtTime(modal.existing.sendTime || defaultTime)} IST</span></p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Chapter</label>
                  <select
                    value={selectedChapter}
                    onChange={(e) => { setSelectedChapter(e.target.value); setPreview(null); }}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm outline-none focus:border-amber-400"
                  >
                    {CHAPTERS.map((ch) => (
                      <option key={ch.number} value={String(ch.number).padStart(2, '0')}>
                        {ch.number}. {ch.titleMarathi}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Ovi Number <span className="font-normal text-gray-400">(1–{maxOvi})</span>
                  </label>
                  <input
                    type="number"
                    value={oviNum}
                    onChange={(e) => { setOviNum(e.target.value); setPreview(null); }}
                    placeholder={`1–${maxOvi}`}
                    min={1}
                    max={maxOvi}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm outline-none focus:border-amber-400"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 items-end">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Send time (IST)</label>
                  <input
                    type="time"
                    value={dayTime}
                    step={900}
                    onChange={(e) => setDayTime(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm outline-none focus:border-amber-400"
                  />
                </div>
                {/* Its own cell, not squeezed beside the number field: there
                    it overflowed the modal. */}
                <button
                  onClick={loadPreview}
                  disabled={!oviNum || previewLoading}
                  className="py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 disabled:opacity-50"
                >
                  {previewLoading ? 'Loading…' : 'Preview verse'}
                </button>
              </div>
              {dayTime !== defaultTime && (
                <p className="text-xs text-amber-700">This day will send at {fmtTime(dayTime)} instead of the usual {fmtTime(defaultTime)}.</p>
              )}

              {error && <p className="text-red-500 text-sm">{error}</p>}

              {/* The save button stays disabled until the ovi is previewed:
                  Preview is what fetches the verse text from the published
                  book, and that text is what the push carries. Say so,
                  otherwise the greyed-out button looks like the page is broken. */}
              {!preview && !error && (
                <p className="text-xs text-gray-500 leading-relaxed">
                  {oviNum
                    ? (previewLoading ? 'Loading the verse text…' : `Enter a number between 1 and ${maxOvi}.`)
                    : 'Type the ovi number and its text loads from the published book so you can check it before scheduling. The same text is sent in the notification.'}
                </p>
              )}

              {preview && (
                <div className="bg-stone-50 border border-stone-200 rounded-xl p-3">
                  <p className="text-xs text-stone-500 mb-2">This is the text users will receive at {fmtTime(dayTime || defaultTime)} IST on {format(modal.date, 'd MMMM')}.</p>
                  <p className="text-xs font-semibold text-stone-500 mb-1.5">{preview.id}</p>
                  <p className="text-sm text-gray-800 leading-relaxed">{preview.textMarathi}</p>
                  {preview.arthMarathi && (
                    <p className="text-xs text-gray-500 mt-2 leading-relaxed">{preview.arthMarathi.slice(0, 120)}…</p>
                  )}
                </div>
              )}

              <div className="flex gap-3 pt-2">
                {modal.existing && (
                  <button
                    onClick={handleRemove}
                    disabled={saving}
                    className="flex-1 py-2.5 border border-red-200 text-red-600 rounded-xl text-sm font-medium hover:bg-red-50 disabled:opacity-50"
                  >
                    Remove
                  </button>
                )}
                <button
                  onClick={handleSave}
                  disabled={saving || !oviNum || !preview}
                  className="flex-1 py-2.5 bg-amber-600 text-white rounded-xl text-sm font-semibold hover:bg-amber-700 disabled:opacity-50"
                >
                  {saving ? 'Saving…' : 'Schedule Verse'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
